import { useMemo, useState } from "react";
import { dailyDifficultyLabel } from "../../shared/nonogram/daily";
import { ARCHIVE_DAYS, dailyDatePath, recentDailyDates } from "../../shared/seo/dailyDate";
import { seoPageForPath } from "../../shared/seo/pages";
import { SeoContent } from "../components/SeoContent";
import { loadStats } from "../lib/storage";

function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function parseUtc(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

function formatUtc(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addUtcDays(date: string, days: number): string {
  const next = parseUtc(date);
  next.setUTCDate(next.getUTCDate() + days);
  return formatUtc(next);
}

function monthLabel(year: number, month: number): string {
  return new Date(Date.UTC(year, month, 1)).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

type DayCell = {
  date: string;
  inMonth: boolean;
  isToday: boolean;
  isFuture: boolean;
  isAvailable: boolean;
  completed: boolean;
};

function buildMonth(year: number, month: number, completed: Set<string>, today: string, earliest: string): DayCell[] {
  const first = new Date(Date.UTC(year, month, 1));
  const startWeekday = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: DayCell[] = [];

  for (let index = 0; index < startWeekday; index += 1) {
    cells.push({
      date: "",
      inMonth: false,
      isToday: false,
      isFuture: false,
      isAvailable: false,
      completed: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = formatUtc(new Date(Date.UTC(year, month, day)));
    const isFuture = date > today;
    const isAvailable = !isFuture && date >= earliest && date <= today;
    cells.push({
      date,
      inMonth: true,
      isToday: date === today,
      isFuture,
      isAvailable,
      completed: completed.has(date),
    });
  }

  while (cells.length % 7 !== 0) {
    cells.push({
      date: "",
      inMonth: false,
      isToday: false,
      isFuture: false,
      isAvailable: false,
      completed: false,
    });
  }

  return cells;
}

export function Archive() {
  const page = seoPageForPath("/daily-nonogram/archive");
  const today = utcToday();
  const earliest = addUtcDays(today, -(ARCHIVE_DAYS - 1));
  const stats = useMemo(() => loadStats(), []);
  const completed = useMemo(() => new Set(stats.completedDailyDates), [stats.completedDailyDates]);
  const todayDate = parseUtc(today);
  const [cursor, setCursor] = useState({ year: todayDate.getUTCFullYear(), month: todayDate.getUTCMonth() });

  const earliestDate = parseUtc(earliest);
  const canPrev =
    cursor.year > earliestDate.getUTCFullYear() ||
    (cursor.year === earliestDate.getUTCFullYear() && cursor.month > earliestDate.getUTCMonth());
  const canNext =
    cursor.year < todayDate.getUTCFullYear() ||
    (cursor.year === todayDate.getUTCFullYear() && cursor.month < todayDate.getUTCMonth());

  const cells = buildMonth(cursor.year, cursor.month, completed, today, earliest);
  const listDates = recentDailyDates(ARCHIVE_DAYS, today);

  function shiftMonth(delta: number): void {
    const next = new Date(Date.UTC(cursor.year, cursor.month + delta, 1));
    setCursor({ year: next.getUTCFullYear(), month: next.getUTCMonth() });
  }

  return (
    <main className="prose-page archive-page" id="main-content">
      <p className="eyebrow">{page?.eyebrow ?? "Thirty days of quiet clues"}</p>
      <h1>{page?.heading ?? "Daily Nonogram archive"}</h1>
      <p className="lede">{page?.lede}</p>

      <section className="archive-calendar" aria-label="Daily puzzle calendar">
        <div className="calendar-nav">
          <button className="text-button" type="button" disabled={!canPrev} onClick={() => shiftMonth(-1)} aria-label="Previous month">
            ← Prev
          </button>
          <h2>{monthLabel(cursor.year, cursor.month)}</h2>
          <button className="text-button" type="button" disabled={!canNext} onClick={() => shiftMonth(1)} aria-label="Next month">
            Next →
          </button>
        </div>
        <div className="calendar-weekdays" aria-hidden="true">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>
        <div className="calendar-grid" role="grid" aria-label={monthLabel(cursor.year, cursor.month)}>
          {cells.map((cell, index) => {
            if (!cell.inMonth) return <div className="calendar-day is-empty" key={`empty-${index}`} />;
            const className = [
              "calendar-day",
              cell.isToday ? "is-today" : "",
              cell.completed ? "is-completed" : "",
              cell.isFuture || !cell.isAvailable ? "is-disabled" : "",
            ].filter(Boolean).join(" ");

            const check = cell.completed ? <span className="calendar-check" aria-hidden="true">✓</span> : null;

            if (cell.isFuture || (!cell.isAvailable && !cell.isToday)) {
              return (
                <div className={className} key={cell.date} aria-disabled="true">
                  <span>{Number(cell.date.slice(-2))}</span>
                  {check}
                </div>
              );
            }

            return (
              <a
                className={className}
                key={cell.date}
                href={dailyDatePath(cell.date)}
                aria-label={`${cell.date}${cell.completed ? ", completed" : ""}${cell.isToday ? ", today" : ""}`}
              >
                <span>{Number(cell.date.slice(-2))}</span>
                {check}
              </a>
            );
          })}
        </div>
        <p className="calendar-legend">
          <span className="legend-today">Today</span>
          <span className="legend-done">Completed</span>
          <span className="legend-open">Available</span>
        </p>
      </section>

      <ol className="archive-list">
        {listDates.map((date) => (
          <li key={date}>
            <a href={dailyDatePath(date)}>
              <time dateTime={date}>{date}</time>
              <span>{dailyDifficultyLabel(date)}{completed.has(date) ? " · Done" : ""}</span>
              <b>Play <span aria-hidden="true">→</span></b>
            </a>
          </li>
        ))}
      </ol>
      <SeoContent path="/daily-nonogram/archive" />
    </main>
  );
}
